import { notifyError } from "@/lib/notify-error";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import {
  BarChart3,
  Check,
  GanttChartSquare,
  Plus,
  Table2,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { statusTokenClasses } from "@/components/status-badge";
import { StatusDot } from "@/components/status-dot";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { Toggle } from "@/components/ui/toggle";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  FIELD_PILOT_COLUMN_BAND,
  FIELD_PILOT_ROW_CELL,
  FieldPilotTreeCellContent,
  fieldPilotRowClasses,
} from "@/features/field-pilot/row-hierarchy";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";
import { cn } from "@/lib/utils";
import {
  compareScheduleRows,
  formatDurationDays,
  formatSchedulePercent,
  scheduleStatusDot,
  withFreshScheduleTiming,
  type FieldPilotScheduleRow,
} from "@/features/field-pilot/schedule-format";
import { useScheduleClock } from "@/features/field-pilot/use-schedule-clock";
import {
  ScheduleGanttView,
  ScheduleProgressView,
} from "@/features/field-pilot/schedule-visualizations";
import {
  splitTaskChildRows,
  SubTaskRows,
  UngroupedRow,
  type FieldPilotScheduleRowWithSubTasks,
} from "@/features/field-pilot/sub-task-rows";
import {
  FieldPilotSubTaskDialog,
  type FieldPilotSubTaskDialogTarget,
} from "@/features/field-pilot/sub-task-dialog";

// Re-exported for the route and visualizations that import from this module.
export { scheduleStatusDot };

type ScheduleView = "edit" | "progress" | "gantt";

const SCHEDULE_VIEWS: { id: ScheduleView; label: string; icon: typeof Table2 }[] = [
  { id: "edit", label: "Edit", icon: Table2 },
  { id: "progress", label: "Progress", icon: BarChart3 },
  { id: "gantt", label: "Gantt", icon: GanttChartSquare },
];

interface FieldPilotScheduleDialogProps {
  jobId: Id<"fieldPilotJobs"> | null;
  jobName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FieldPilotScheduleDialog({
  jobId,
  jobName,
  open,
  onOpenChange,
}: FieldPilotScheduleDialogProps) {
  const scheduleNow = useScheduleClock(open);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data: scheduleRows = [] } = useQuery(
    convexQuery(
      api.fieldPilot.progress.getScheduleByJob,
      open && jobId ? { jobId, timeZone } : "skip"
    )
  );
  const { data: statuses = [] } = useQuery(
    convexQuery(
      api.fieldPilot.statuses.listByJob,
      open && jobId ? { jobId } : "skip"
    )
  );

  const setDisplayName = useMutation(api.fieldPilot.tags.setDisplayName);
  const setSchedule = useMutation(api.fieldPilot.tags.setSchedule);
  const removeCustom = useMutation(api.fieldPilot.tags.removeCustom);
  const createCustom = useMutation(api.fieldPilot.tags.createCustom);
  const setCompletion = useMutation(api.fieldPilot.statuses.setCompletion);
  const removeEmptySubTask = useMutation(api.fieldPilot.subTasks.deleteEmpty);

  const [newItemName, setNewItemName] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [view, setView] = useState<ScheduleView>("edit");
  const [expandedTagIds, setExpandedTagIds] = useState<Set<string>>(() => new Set());
  const [subTaskDialogTarget, setSubTaskDialogTarget] =
    useState<FieldPilotSubTaskDialogTarget | null>(null);
  const [isSubTaskDialogOpen, setIsSubTaskDialogOpen] = useState(false);

  // Freeze the row order while the dialog is open so editing a date doesn't
  // resort rows out from under the user mid-edit. The order is captured the
  // first time rows load after opening and re-computed when the dialog reopens.
  const [frozenOrder, setFrozenOrder] = useState<string[] | null>(null);

  useEffect(() => {
    if (!open) {
      setFrozenOrder(null);
      setExpandedTagIds(new Set());
    }
  }, [open]);

  const toggleExpandTag = (tagId: string) => {
    setExpandedTagIds((current) => {
      const next = new Set(current);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  };

  useEffect(() => {
    if (open && frozenOrder === null && scheduleRows.length > 0) {
      const order = [...(scheduleRows as FieldPilotScheduleRow[])]
        .sort(compareScheduleRows)
        .map((row) => row.tagId);
      setFrozenOrder(order);
    }
  }, [open, frozenOrder, scheduleRows]);

  const sortedRows = useMemo(() => {
    const rows = (scheduleRows as FieldPilotScheduleRowWithSubTasks[]).map((row) => ({
      // Refresh the parent row and each nested child against the client clock.
      ...withFreshScheduleTiming(row, scheduleNow),
      subTasks: (row.subTasks ?? []).map((subTask) => withFreshScheduleTiming(subTask, scheduleNow)),
      ungrouped: row.ungrouped ? withFreshScheduleTiming(row.ungrouped, scheduleNow) : row.ungrouped,
    }));
    if (frozenOrder === null) {
      return rows.sort(compareScheduleRows);
    }
    const orderIndex = new Map(frozenOrder.map((id, index) => [id, index]));
    // Keep frozen rows in their captured order; newly added rows (not yet in
    // the frozen order) sort to the end so they stay put until the next reopen.
    return rows.sort((a, b) => {
      const ai = orderIndex.get(a.tagId);
      const bi = orderIndex.get(b.tagId);
      if (ai !== undefined && bi !== undefined) return ai - bi;
      if (ai !== undefined) return -1;
      if (bi !== undefined) return 1;
      return compareScheduleRows(a, b);
    });
  }, [scheduleRows, frozenOrder, scheduleNow]);
  // Mirror the backend fallback used for tasks without dated milestones: jobs with no explicit
  // completion flags fall back to the status named "Installed".
  const usesLegacyCompletion = statuses.every(
    (status) => status.isCompletionStatus === undefined
  );

  const handleAddLineItem = async () => {
    if (isAdding) return;
    if (!jobId) return;
    const trimmed = newItemName.trim();
    if (!trimmed) return;
    setIsAdding(true);
    try {
      await createCustom({ jobId, rawValue: trimmed });
      setNewItemName("");
    } catch (error) {
      notifyError(error,
        getUserFacingErrorMessage(error, "Failed to add custom line item")
      );
    } finally {
      setIsAdding(false);
    }
  };

  const handleEditSubTask = (subTaskId: Id<"fieldPilotSubTasks">) => {
    let subTaskName = "";
    for (const row of sortedRows) {
      const match = row.subTasks?.find(
        (subTask) => String(subTask.subTaskId) === String(subTaskId)
      );
      if (match) {
        subTaskName = match.name;
        break;
      }
    }
    setSubTaskDialogTarget({ mode: "edit", subTaskId, subTaskName });
    setIsSubTaskDialogOpen(true);
  };

  const handleGroupUngrouped = (row: FieldPilotScheduleRowWithSubTasks) => {
    setSubTaskDialogTarget({
      mode: "create",
      tagId: row.tagId,
      tagName: row.displayName,
      groupUngrouped: true,
      ungroupedItemCount: row.ungrouped?.itemCount ?? 0,
      distinctTagCount: 1,
    });
    setIsSubTaskDialogOpen(true);
  };

  const handleDeleteEmptySubTask = (subTaskId: Id<"fieldPilotSubTasks">) => {
    removeEmptySubTask({ subTaskId })
      .then(() => toast.success("Sub-task deleted"))
      .catch((error) => notifyError(error, getUserFacingErrorMessage(error, "Failed to delete sub-task")));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[85dvh] max-h-[85dvh] w-[85vw] max-w-[85vw] flex-col overflow-hidden p-0 sm:max-w-[85vw]">
        <DialogHeader className="border-b px-5 py-3">
          <DialogTitle>Schedule</DialogTitle>
          <DialogDescription>
            {jobName
              ? `Plan start and finish dates for ${jobName}. Set it up once — progress tracks automatically.`
              : "Plan start and finish dates per tag. Set it up once — progress tracks automatically."}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Counts as complete
            </Label>
            {statuses.map((status) => {
              const active = usesLegacyCompletion
                ? status.name === "Installed"
                : status.isCompletionStatus ?? false;
              return (
                <Toggle
                  key={status._id}
                  variant="outline"
                  size="compact"
                  className="px-2"
                  pressed={active}
                  onPressedChange={() =>
                    setCompletion({
                      statusId: status._id,
                      isCompletionStatus: !active,
                    }).catch((error) =>
                      notifyError(error,
                        getUserFacingErrorMessage(error, "Failed to update completion status")
                      )
                    )
                  }
                >
                  <StatusDot colorHex={status.colorHex} />
                  {status.name}
                  {active ? <Check className="size-3.5" /> : null}
                </Toggle>
              );
            })}
            <span className="text-xs text-muted-foreground">
              These statuses define 100% completion for tasks without dated milestones.
            </span>
          </div>

          <div className="mb-4 rounded-lg border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">How progress is calculated: </span>
            Actual progress measures work toward final completion. For milestone tasks, each dated
            milestone carries an equal share of 100%, and items earn that share when their status
            reaches the stage. Plan by today interpolates from the start through each milestone due
            date. Count or measured quantity follows the task&apos;s selected progress mode.
          </div>

          <SegmentedControl
            aria-label="Schedule view"
            className="mb-4"
            size="compact"
            value={view}
            onValueChange={setView}
            options={SCHEDULE_VIEWS.map(({ id, label, icon }) => ({ value: id, label, icon }))}
          />

          {view === "progress" ? (
            <ScheduleProgressView rows={sortedRows} />
          ) : view === "gantt" ? (
            <ScheduleGanttView rows={sortedRows} />
          ) : sortedRows.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              No tags selected yet. Select tags or add a custom line item below to plan
              their schedule.
            </div>
          ) : (
            <div className="rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[120px]">Tag</TableHead>
                    <TableHead className="min-w-[260px]">Task Name</TableHead>
                    <TableHead className={FIELD_PILOT_COLUMN_BAND}>Start</TableHead>
                    <TableHead>Finish</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead title="Expected progress toward final completion as of today">
                      Expected %
                    </TableHead>
                    <TableHead title="Actual progress toward final completion from item statuses">
                      Actual %
                    </TableHead>
                    <TableHead className="text-center">On Track</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRows.map((row, index) => {
                    const isExpanded = expandedTagIds.has(String(row.tagId));
                    const subTasks = row.subTasks ?? [];
                    const {
                      items: soloSubTasks,
                      groups: groupSubTasks,
                      hasChildRows,
                    } = splitTaskChildRows(subTasks, row.ungrouped);
                    return (
                      <Fragment key={row.tagId}>
                        <ScheduleRowEditor
                          row={row}
                          hasSubTasks={hasChildRows}
                          isExpanded={isExpanded}
                          startsGroup={index > 0}
                          onToggleExpand={() => toggleExpandTag(String(row.tagId))}
                          onDisplayNameSave={(displayName) =>
                            setDisplayName({ tagId: row.tagId, displayName }).catch(
                              (error) =>
                                notifyError(error,
                                  getUserFacingErrorMessage(error, "Failed to update name")
                                )
                            )
                          }
                          onScheduleChange={(args) =>
                            setSchedule({ tagId: row.tagId, ...args }).catch((error) =>
                              notifyError(error,
                                getUserFacingErrorMessage(error, "Failed to update dates")
                              )
                            )
                          }
                          onRemove={() =>
                            removeCustom({ tagId: row.tagId })
                              .then(() => toast.success("Custom line item removed"))
                              .catch((error) =>
                                notifyError(error,
                                  getUserFacingErrorMessage(error, "Failed to remove line item")
                                )
                              )
                          }
                        />
                        {/* Item schedules first, then grouped sub-tasks — both revealed together
                            by the task's toggle. */}
                        {isExpanded && soloSubTasks.length > 0 ? (
                          <SubTaskRows
                            subTasks={soloSubTasks}
                            layout={{ variant: "schedule" }}
                            onEditSubTask={handleEditSubTask}
                            onDeleteSubTask={handleDeleteEmptySubTask}
                          />
                        ) : null}
                        {isExpanded && groupSubTasks.length > 0 ? (
                          <SubTaskRows
                            subTasks={groupSubTasks}
                            layout={{ variant: "schedule" }}
                            onEditSubTask={handleEditSubTask}
                            onDeleteSubTask={handleDeleteEmptySubTask}
                          />
                        ) : null}
                        {/* The ungrouped remainder closes the group, so the children above it
                            plus this row account for the whole task. */}
                        {isExpanded && row.ungrouped ? (
                          <UngroupedRow
                            ungrouped={row.ungrouped}
                            layout={{ variant: "schedule" }}
                            onGroup={() => handleGroupUngrouped(row)}
                          />
                        ) : null}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="mt-4 flex items-center gap-2">
            <Input
              className="h-8 max-w-xs"
              value={newItemName}
              onChange={(event) => setNewItemName(event.target.value)}
              placeholder="Add custom line item"
              disabled={isAdding}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void handleAddLineItem();
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={isAdding || !newItemName.trim()}
              onClick={() => void handleAddLineItem()}
            >
              <Plus className="size-3.5" />
              Add
            </Button>
          </div>
        </div>
      </DialogContent>

      {jobId && subTaskDialogTarget ? (
        <FieldPilotSubTaskDialog
          open={isSubTaskDialogOpen}
          onOpenChange={setIsSubTaskDialogOpen}
          jobId={jobId}
          statuses={statuses}
          target={subTaskDialogTarget}
        />
      ) : null}
    </Dialog>
  );
}

function ScheduleRowEditor({
  row,
  hasSubTasks,
  isExpanded,
  startsGroup,
  onToggleExpand,
  onDisplayNameSave,
  onScheduleChange,
  onRemove,
}: {
  row: FieldPilotScheduleRow;
  hasSubTasks: boolean;
  isExpanded: boolean;
  startsGroup: boolean;
  onToggleExpand: () => void;
  onDisplayNameSave: (displayName: string) => void;
  onScheduleChange: (args: {
    startDateKey?: string | null;
    finishDateKey?: string | null;
  }) => void;
  onRemove: () => void;
}) {
  const [displayName, setLocalDisplayName] = useState(row.displayName);
  useEffect(() => {
    setLocalDisplayName(row.displayName);
  }, [row.displayName]);

  const dot = scheduleStatusDot(row);

  return (
    <TableRow
      className={fieldPilotRowClasses({
        kind: "task",
        startsGroup,
        isBehind: row.isBehind,
      })}
    >
      <TableCell className={FIELD_PILOT_ROW_CELL}>
        {/* Linked tag is fixed — the user can rename the task but not change the link. */}
        <span
          className={cn(
            "inline-flex max-w-full items-center rounded-md border px-2 py-1 text-xs font-semibold",
            row.isCustom
              ? "border-dashed text-muted-foreground"
              : "bg-muted/50"
          )}
          title={row.isCustom ? "Custom line item" : `Linked tag ${row.rawValue}`}
        >
          <span className="truncate">{row.isCustom ? "Custom" : row.rawValue}</span>
        </span>
      </TableCell>
      <TableCell className={FIELD_PILOT_ROW_CELL}>
        <FieldPilotTreeCellContent
          kind="task"
          isExpanded={isExpanded}
          onToggle={hasSubTasks ? onToggleExpand : undefined}
          toggleLabel={isExpanded ? "Hide items and sub-tasks" : "Show items and sub-tasks"}
        >
          <Input
            className="h-8"
            value={displayName}
            onChange={(event) => setLocalDisplayName(event.target.value)}
            onBlur={() => {
              const trimmed = displayName.trim();
              if (trimmed !== row.displayName) {
                onDisplayNameSave(trimmed);
              }
            }}
            placeholder={row.rawValue}
            aria-label={`Task name for ${row.rawValue}`}
          />
        </FieldPilotTreeCellContent>
      </TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, FIELD_PILOT_COLUMN_BAND)}>
        <DatePicker
          className="w-[150px]"
          size="sm"
          value={row.startDateKey ?? ""}
          onChange={(value) =>
            onScheduleChange({ startDateKey: value || null })
          }
          aria-label={`Start date for ${row.rawValue}`}
        />
      </TableCell>
      <TableCell className={FIELD_PILOT_ROW_CELL}>
        <DatePicker
          className="w-[150px]"
          size="sm"
          value={row.finishDateKey ?? ""}
          onChange={(value) =>
            onScheduleChange({ finishDateKey: value || null })
          }
          aria-label={`Finish date for ${row.rawValue}`}
        />
      </TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "whitespace-nowrap")}>
        {formatDurationDays(row.durationDays)}
      </TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "whitespace-nowrap")}>
        {row.hasSchedule ? formatSchedulePercent(row.expectedFraction) : "—"}
      </TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "whitespace-nowrap font-semibold")}>
        {formatSchedulePercent(row.actualFraction)}
      </TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "text-center")}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={statusTokenClasses(dot.status, {
                visual: "dot",
                size: "lg",
                tone: dot.tone,
              })}
              role="img"
              aria-label={dot.label}
            />
          </TooltipTrigger>
          <TooltipContent>{dot.label}</TooltipContent>
        </Tooltip>
      </TableCell>
      <TableCell className={FIELD_PILOT_ROW_CELL}>
        {row.isCustom ? (
          <IconButton label={`Remove ${row.rawValue}`} icon={Trash2} onClick={onRemove} />
        ) : null}
      </TableCell>
    </TableRow>
  );
}
